import { useMemo } from "react";
import { CheckCircle2, XCircle, Loader2, ExternalLink, Workflow } from "lucide-react";

const BUBBLE_GRADIENTS = [
  "linear-gradient(135deg, rgba(125,92,240,0.09) 0%, rgba(110,78,220,0.04) 100%)",
  "linear-gradient(150deg, rgba(130,95,245,0.08) 0%, rgba(115,82,228,0.04) 100%)",
  "linear-gradient(120deg, rgba(120,88,235,0.09) 0%, rgba(128,95,240,0.04) 100%)",
];

function pickGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  return BUBBLE_GRADIENTS[Math.abs(hash) % BUBBLE_GRADIENTS.length]!;
}

function isUrl(value: string): boolean {
  return value.startsWith("https://") || value.startsWith("http://");
}

type Status = "dispatching" | "queued" | "running" | "success" | "failure" | "error";

interface WorkflowRunData {
  run_key?: string;
  workflow_name?: string;
  filename?: string;
  description?: string;
  status?: Status;
  inputs?: Record<string, string>;
  run_id?: number | null;
  run_url?: string | null;
  duration_s?: number | null;
  conclusion?: string | null;
  outputs?: Record<string, string | null>;
  error?: string | null;
}

interface WorkflowRunWidgetProps {
  data: WorkflowRunData;
}

const STATUS_LABEL: Record<Status, string> = {
  dispatching: "Dispatching",
  queued: "Queued",
  running: "Running",
  success: "Success",
  failure: "Failed",
  error: "Error",
};

export function WorkflowRunWidget({ data }: WorkflowRunWidgetProps) {
  if (!data) return null;
  const status: Status = (data.status as Status) ?? "dispatching";
  const inProgress = status === "dispatching" || status === "queued" || status === "running";
  const failed = status === "failure" || status === "error";
  const succeeded = status === "success";

  const gradient = useMemo(
    () => pickGradient(data.run_key ?? data.workflow_name ?? "workflow"),
    [data.run_key, data.workflow_name],
  );

  const inputs = data.inputs ?? {};
  const inputEntries = Object.entries(inputs);
  const outputs = data.outputs ?? {};
  const outputEntries = Object.entries(outputs);

  return (
    <div
      className="animate-slide-up rounded-3xl rounded-bl-lg overflow-hidden max-w-2xl"
      style={{ background: gradient }}
    >
      <div className="px-4 py-3">
        <div className="flex items-center gap-2">
          {inProgress && <Loader2 size={14} className="animate-spin text-daydra-dim" />}
          {succeeded && <CheckCircle2 size={14} className="text-daydra-success" />}
          {failed && <XCircle size={14} className="text-daydra-error" />}
          <span
            className={`text-sm font-semibold ${
              succeeded
                ? "text-daydra-success"
                : failed
                  ? "text-daydra-error"
                  : "text-daydra-text"
            }`}
          >
            {STATUS_LABEL[status]}
          </span>
          <span className="text-2xs text-daydra-muted font-mono inline-flex items-center gap-1">
            <Workflow size={10} className="shrink-0" />
            {data.workflow_name ?? data.filename ?? "workflow"}
          </span>
          {typeof data.duration_s === "number" && data.duration_s > 0 && (
            <span className="text-2xs text-daydra-muted font-mono ml-auto">
              {data.duration_s.toFixed(1)}s
            </span>
          )}
        </div>

        {data.description && (
          <p className="text-xs text-daydra-dim mt-1">{data.description}</p>
        )}
      </div>

      {inputEntries.length > 0 && (
        <div className="px-4 py-2 border-t border-white/[0.06]">
          <p className="text-2xs text-daydra-muted font-mono uppercase tracking-wide mb-1">
            inputs
          </p>
          <ul className="text-xs font-mono space-y-0.5">
            {inputEntries.map(([k, v]) => (
              <li key={k} className="flex gap-2">
                <span className="text-daydra-muted shrink-0">{k}</span>
                <span className="text-daydra-dim break-all">{String(v)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {succeeded && outputEntries.length > 0 && (
        <div className="px-4 py-2 border-t border-white/[0.06]">
          <p className="text-2xs text-daydra-muted font-mono uppercase tracking-wide mb-1">
            outputs
          </p>
          <ul className="text-xs font-mono space-y-0.5">
            {outputEntries.map(([k, v]) => (
              <li key={k} className="flex gap-2">
                <span className="text-daydra-muted shrink-0">{k}</span>
                {v === null ? (
                  <span className="text-daydra-dim break-all">(not emitted)</span>
                ) : isUrl(v) ? (
                  <a
                    href={v}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-daydra-accent hover:text-daydra-accent/80 underline break-all"
                  >
                    {v}
                  </a>
                ) : (
                  <span className="text-daydra-dim break-all">{v}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {failed && data.error && (
        <div className="px-4 py-2 border-t border-white/[0.06]">
          <p className="text-2xs text-daydra-muted font-mono uppercase tracking-wide mb-1">
            error
          </p>
          <pre className="text-xs font-mono text-daydra-error/90 whitespace-pre-wrap break-all">
            {data.error}
          </pre>
        </div>
      )}

      {data.run_url && (
        <a
          href={data.run_url}
          target="_blank"
          rel="noreferrer noopener"
          className="flex items-center gap-1.5 px-4 py-2 text-2xs text-daydra-muted hover:text-daydra-text border-t border-white/[0.06] transition-colors"
        >
          <ExternalLink size={11} />
          <span>View run on GitHub</span>
          {typeof data.run_id === "number" && (
            <span className="font-mono ml-auto">#{data.run_id}</span>
          )}
        </a>
      )}
    </div>
  );
}
